<template>
  <div class="custom-order-management">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <div class="el-loading-spinner">
        <i class="el-icon-loading"></i>
        <p class="el-loading-text">加载中...</p>
      </div>
    </div>
    
    <!-- 错误提示 -->
    <div v-if="error" class="error-message">
      <el-alert :title="error" type="error" :closable="false" show-icon />
    </div>

    <!-- 数据统计 -->
    <div class="stats-info" v-if="!loading && !error">
      <p>共找到 {{ orders.length }} 条定制</p>
    </div>

    <!-- 定制卡片列表 -->
    <div class="orders-grid">
      <el-card 
        v-for="item in orders" 
        :key="item.id" 
        class="order-card"
        :class="{ 'is-rejected': item.status === 'rejected' }"
        @click="showDetail(item)"
      >
        <template #header>
          <div class="card-header">
            <span class="title">定制 #{{ item.id }}</span>
            <div class="card-actions">
              <el-tag :type="getStatusType(item.status)" size="small">
                {{ getStatusText(item.status) }}
              </el-tag>
              <el-button
                type="danger"
                size="small"
                @click.stop="handleDelete(item)"
                class="delete-btn"
              >删除</el-button>
            </div>
          </div>
        </template>
        
        <div class="card-content">
          <div class="user-info">
            <p><strong>提交者:</strong> {{ item.user_name || '未知用户' }}</p>
            <p><strong>联系方式:</strong> {{ item.contact }}</p>
          </div>
          
          <div class="images-preview">
            <div class="preview-section">
              <h4>头部参考</h4>
              <div class="preview-image-container">
                <el-image
                  v-if="parseImages(item.head_images_url)[0]"
                  :src="parseImages(item.head_images_url)[0]"
                  fit="contain"
                  class="preview-image"
                />
                <div v-else class="no-image">暂无图片</div>
              </div>
            </div>
            <div class="preview-section">
              <h4>上身参考</h4>
              <div class="preview-image-container">
                <el-image
                  v-if="parseImages(item.body_images_url)[0]"
                  :src="parseImages(item.body_images_url)[0]"
                  fit="contain"
                  class="preview-image"
                />
                <div v-else class="no-image">暂无图片</div>
              </div>
            </div>
            <div class="preview-section">
              <h4>下身参考</h4>
              <div class="preview-image-container">
                <el-image
                  v-if="parseImages(item.leg_images_url)[0]"
                  :src="parseImages(item.leg_images_url)[0]"
                  fit="contain"
                  class="preview-image"
                />
                <div v-else class="no-image">暂无图片</div>
              </div>
            </div>
          </div>
          
          <p class="description">{{ item.description }}</p>
        </div>
      </el-card>
    </div>

    <!-- 详情弹窗 -->
    <el-dialog
      v-model="detailVisible"
      :title="`定制详情 #${currentOrder?.id}`"
      width="700px"
    >
      <div v-if="currentOrder" class="detail-content">
        <div class="detail-section">
          <h4>状态</h4>
          <el-tag :type="getStatusType(currentOrder.status)">
            {{ getStatusText(currentOrder.status) }}
          </el-tag>
          <span v-if="currentOrder.status === 'rejected'" class="reject-reason">
            ({{ currentOrder.reject_reason }})
          </span>
        </div>

        <div class="detail-section">
          <h4>提交者</h4>
          <p>{{ currentOrder.user_name || '未知用户' }}</p>
        </div>

        <div class="detail-section">
          <h4>联系方式</h4>
          <p>{{ currentOrder.contact }}</p>
        </div>

        <div class="detail-section">
          <h4>参考图片</h4>
          <div class="detail-images">
            <div class="image-section">
              <h4>头部参考</h4>
              <div class="image-container">
                <el-image
                  v-if="parseImages(currentOrder.head_images_url)[0]"
                  :src="parseImages(currentOrder.head_images_url)[0]"
                  fit="contain"
                  :preview-src-list="parseImages(currentOrder.head_images_url)"
                />
                <div v-else class="no-image">暂无图片</div>
              </div>
            </div>
            <div class="image-section">
              <h4>上身参考</h4>
              <div class="image-container">
                <el-image
                  v-if="parseImages(currentOrder.body_images_url)[0]"
                  :src="parseImages(currentOrder.body_images_url)[0]"
                  fit="contain"
                  :preview-src-list="parseImages(currentOrder.body_images_url)"
                />
                <div v-else class="no-image">暂无图片</div>
              </div>
            </div>
            <div class="image-section">
              <h4>下身参考</h4>
              <div class="image-container">
                <el-image
                  v-if="parseImages(currentOrder.leg_images_url)[0]"
                  :src="parseImages(currentOrder.leg_images_url)[0]"
                  fit="contain"
                  :preview-src-list="parseImages(currentOrder.leg_images_url)"
                />
                <div v-else class="no-image">暂无图片</div>
              </div>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h4>需求描述</h4>
          <div class="description-box">
            {{ currentOrder.description }}
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="detail-footer">
          <el-button-group v-if="currentOrder.status === 'pending'">
            <el-button type="primary" @click="handleProcess(currentOrder)">处理</el-button>
            <el-button type="danger" @click="handleReject(currentOrder)">拒绝</el-button>
          </el-button-group>
          <el-button 
            v-if="currentOrder.status === 'processing'"
            type="success" 
            @click="handleComplete(currentOrder)"
          >完成</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox, ElLoading } from 'element-plus'
import requestV2 from '@/utils/requestV2';

interface CustomOrder {
  id: number
  user_id: number
  user_name?: string
  description: string
  head_images: string
  body_images: string
  leg_images: string
  head_images_url: string
  body_images_url: string
  leg_images_url: string
  contact: string
  status: 'pending' | 'processing' | 'completed' | 'rejected'
  reject_reason?: string
  created_at: string
}


const currentPage = ref(1)
const total = ref(0)
const orders = ref<CustomOrder[]>([])
const currentOrder = ref<CustomOrder | null>(null)
const detailVisible = ref(false)
const loading = ref(false)
const error = ref('')

// 获取定制列表
const fetchOrders = async () => {
  loading.value = true
  error.value = ''
  
  try {
    const response = await requestV2.get(`/system/custom/custom-orders?page=${currentPage.value}`)
    
    if (!(response.data.code == 200)) {
      throw new Error('获取定制列表失败')
    }
    
    const data = await response.data.data;
    orders.value = data.records
    total.value = data.total
  } catch (err) {
    console.error('获取定制列表失败:', err)
    error.value = err instanceof Error ? err.message : '获取定制列表失败'
    ElMessage.error(error.value)
  } finally {
    loading.value = false
  }
}

// 显示详情
const showDetail = (order: CustomOrder) => {
  currentOrder.value = order
  detailVisible.value = true
}

// 处理定制
const handleProcess = async (order: CustomOrder) => {
  try {
    const response = await requestV2.post(`/system/custom/custom-orders/${order.id}/status`, JSON.stringify({
        status: 'processing'
      }))

    if (!(response.data.code == 200)) {
      throw new Error('处理定制失败')
    }

    ElMessage.success('定制已接受')
    await fetchOrders()
  } catch (error) {
    console.error('处理定制失败:', error)
    ElMessage.error('处理定制失败')
  }
}

// 完成定制
const handleComplete = async (order: CustomOrder) => {
  try {
    const response = await requestV2.post(`/system/custom/custom-orders/${order.id}/status`, JSON.stringify({
        status: 'completed'
      }))

    if (!(response.data.code == 200)) {
      throw new Error('完成定制失败')
    }

    ElMessage.success('定制已完成')
    await fetchOrders()
  } catch (error) {
    console.error('完成定制失败:', error)
    ElMessage.error('完成定制失败')
  }
}

// 拒绝定制
const handleReject = async (order: CustomOrder) => {
  try {
    const { value: reason } = await ElMessageBox.prompt('请输入拒绝原因', '拒绝定制', {
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })
    
    if (!reason) return
    
    const response = await requestV2.post(`/system/custom/custom-orders/${order.id}/status`, JSON.stringify({
        status: 'rejected',
        reject_reason: reason
      }))

    if (!(response.data.code == 200)) {
      throw new Error('拒绝定制失败')
    }

    ElMessage.success('定制已拒绝')
    await fetchOrders()
  } catch (error) {
    console.error('拒绝定制失败:', error)
    ElMessage.error('拒绝定制失败')
  }
}

// 添加删除方法
const handleDelete = async (order: CustomOrder) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除定制订单 #${order.id} 吗？`,
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )

    const response = await requestV2.post(`/system/custom/custom-orders/${order.id}`)

    if (!(response.data.code == 200)) {
      throw new Error('删除失败')
    }

    ElMessage.success('删除成功')
    // 重新获取订单列表
    fetchOrders()
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.toString().includes('cancel')) return
      ElMessage.error('删除失败：' + error.message)
    } else if (typeof error === 'string' && error.includes('cancel')) {
      return
    } else {
      ElMessage.error('删除失败')
    }
  }
}

// 工具函数
const parseImages = (imagesStr: string | null) => {
  if (!imagesStr) return []
  try {
    return JSON.parse(imagesStr)
  } catch (error) {
    console.error('解析图片数据失败:', error)
    return []
  }
}

const getStatusType = (status: string) => {
  const typeMap = {
    pending: 'warning',
    submitted: 'warning',    // 兼容旧状态名
    processing: 'primary',
    accepted: 'primary',     // 兼容旧状态名
    completed: 'success',
    rejected: 'danger'
  }
  return typeMap[status as keyof typeof typeMap] || ''
}

const getStatusText = (status: string) => {
  const textMap = {
    pending: '待处理',
    submitted: '待处理',    // 兼容旧状态名
    processing: '处理中',
    accepted: '处理中',     // 兼容旧状态名
    completed: '已完成',
    rejected: '已拒绝'
  }
  return textMap[status as keyof typeof textMap] || status
}

onMounted(fetchOrders)
</script>

<style scoped>
.custom-order-management {
  padding: 20px;
}

.orders-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.order-card {
  cursor: pointer;
  transition: all 0.3s;
}

.order-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title {
  font-weight: bold;
  color: #333;
}

.user-info {
  margin-bottom: 16px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #409EFF;
}

.user-info p {
  margin: 5px 0;
  font-size: 14px;
}

.images-preview {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 15px;
}

.preview-section h4 {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
  text-align: center;
}

.preview-image-container {
  aspect-ratio: 1;
  background: #f5f7fa;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.description {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.detail-section {
  margin-bottom: 20px;
}

.detail-section h4 {
  font-size: 16px;
  margin-bottom: 8px;
  color: #333;
  font-weight: 600;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.detail-section:nth-child(2) {
  padding: 10px;
  background-color: #f0f9ff;
  border-radius: 4px;
  border-left: 3px solid #409EFF;
}

.detail-images {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.image-container {
  aspect-ratio: 1;
  background: #f5f7fa;
  border-radius: 4px;
  overflow: hidden;
}

.description-box {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 4px;
  white-space: pre-wrap;
}

.detail-footer {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.no-image {
  color: #909399;
  padding: 20px;
  text-align: center;
}

.reject-reason {
  color: #f56c6c;
  margin-left: 8px;
}

.is-rejected {
  opacity: 0.8;
}

.loading-state {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.error-message {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.stats-info {
  padding: 20px;
  background-color: #fff;
  border-radius: 4px;
  margin-bottom: 20px;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.delete-btn {
  opacity: 0.8;
}

.delete-btn:hover {
  opacity: 1;
}
</style> 